import React, { useEffect } from 'react';
import { alpha, makeStyles, useMediaQuery, useTheme } from '~/lib/bn-material-ui';
import classnames from 'classnames';
import _ from 'lodash';
import { DateTime } from 'luxon';
import BinLevelReading from './bin-level-reading';
import BinLevelForecast from './bin-level-forecast';
import BinOrder from './bin-order';
import { useStateValue } from './data/order-calculator-context';
import { getOrderStatusText, OrderStatusIcon } from '../orders/order-status';

const useStyles = makeStyles((theme) => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    daysContainer: {
      display: 'flex',
    },
    day: {
      position: 'relative',
      flex: 1,
      borderTop: `1px solid ${theme.palette.gray[60]}`,
      borderRight: `1px solid ${theme.palette.gray[60]}`,
      borderBottom: `1px solid ${theme.palette.gray[60]}`,
      '&:last-of-type': {
        borderRight: 'none',
      },
    },
    date: {
      background: theme.palette.white,
      padding: '5px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      fontSize: '0.8rem',
      position: 'absolute',
      right: 0,
      left: 0,
      zIndex: 3,
      pointerEvents: 'none',
      borderBottom: `1px solid ${theme.palette.gray[60]}`,
      '& > *': {
        margin: '0 1ch',
      },
    },
    dayTopRow: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    dateNumber: {
      display: 'flex',
      alignItems: 'flex-start',
      fontWeight: 'bold',
    },
    dateHasOrder: {
      background: theme.palette.blue[10],
      height: '3rem',
    },
    dateHasOverfill: {
      backgroundColor: theme.palette.yellow[20],
    },
    dateIsSuggestion: {
      backgroundColor: theme.palette.indigo[60],
      color: theme.palette.white,
      '& .MuiSvgIcon-root': {
        color: theme.palette.white,
      },
    },
    dateIsSuggestionText: {
      color: theme.palette.white,
    },
    month: {
      top: '-1.5rem',
      position: 'absolute',
      fontSize: '1rem',
      margin: 0,
      padding: 0,
      fontWeight: 'bold',
    },
    levels: {
      display: 'flex',
      flexDirection: 'row',
      minHeight: '8rem',
      position: 'relative',
      marginTop: 'calc(3rem + 5%)',
      pointerEvents: 'none',
    },
    hours: {
      position: 'absolute',
      display: 'flex',
      bottom: 0,
      top: 0,
      width: '100%',
      '& > *': {
        minHeight: '120px',
        flex: 1,
        background: theme.palette.gray[30],
        '&:hover': {
          background: alpha(theme.palette.teal[20], 0.6),
          zIndex: 2,
          margin: '-4px -1px',
          borderRadius: '2px',
          cursor: 'pointer',
        },
        '&:focus': {
          outline: 'none',
        },
      },
    },
    gridLines: {
      position: 'absolute',
      height: '100%',
      width: '100%',
      '& > *:nth-child(1)': { bottom: '25%' },
      '& > *:nth-child(2)': { bottom: '50%' },
      '& > *:nth-child(3)': { bottom: '75%' },
      '& > *:nth-child(4)': { bottom: '100%' },
    },
    gridLine: {
      border: `0.1px solid ${theme.palette.gray[40]}`,
      position: 'absolute',
      width: '100%',
      pointerEvents: 'none',
      zIndex: '1',
    },
  };
});

export default function BinLevelWeek({ data, fullTotal, orderForecast }) {
  const styles = useStyles();
  const [{ binLevelDayCount, orders }, dispatch] = useStateValue();

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => dispatch({ type: 'setBinLevelDayCount', binLevelDayCount: isSmall ? 4 : 7 }), [dispatch, isSmall]);

  return (
    <div className={styles.container}>
      <div className={styles.daysContainer}>
        {_.toPairs(data).slice(0, binLevelDayCount).map((day, count) => {
          const [date, intervalData] = day;

          const currentDay = DateTime.fromISO(date);
          const currentISODate = currentDay.toISODate();
          const ordersForDate = orders?.[currentISODate] || [];
          const currentOrder = ordersForDate[0];
          const orderForecastDay = orderForecast.get(date) || {};

          // Do not display overfill notifications for orders in the past
          const hasOverFill = currentISODate >= DateTime.now().toISODate() &&
            Object.keys(orderForecastDay).find(timeSlot => orderForecastDay[timeSlot].volume + intervalData[timeSlot].low > fullTotal);

          return (
            <div key={date} className={styles.day}>
              <div
                className={classnames(
                  styles.date,
                  ordersForDate.length && styles.dateHasOrder,
                  currentOrder?.properties?.state === 'ready' && currentOrder?.properties?.type === 'suggestion' && styles.dateIsSuggestion,
                  hasOverFill && styles.dateHasOverfill,
                )}
              >
                {(count === 0 || currentDay.day === 1) && <span className={styles.month}>{currentDay.monthLong}</span>}
                <div className={classnames(styles.dayTopRow, currentOrder?.properties?.type === 'suggestion' && styles.dateIsSuggestionText)}>
                  <span>{currentDay.toFormat('EEE')}</span>
                  <span className={styles.dateNumber}>
                    <OrderStatusIcon order={currentOrder} hasOverFill={hasOverFill}/>
                    {currentDay.toFormat('d')}
                  </span>
                </div>

                <DayOrdersDescription ordersForDate={ordersForDate}/>
              </div>
              <div className={styles.levels}>
                {intervalData.map((level, i) => level.properties
                  ? <BinLevelReading key={level.getLinkByRel('self').href} level={level} fullTotal={fullTotal} />
                  : <BinLevelForecast key={`${date}${i}`} level={level} fullTotal={fullTotal} orderForecast={(orderForecast.get(date) || {})[i]?.volume || 0} />,
                )}
                <div className={styles.gridLines}>
                  <div className={styles.gridLine} />
                  <div className={styles.gridLine} />
                  <div className={styles.gridLine} />
                  <div className={styles.gridLine} />
                </div>
              </div>
              <div className={styles.hours}>
                {_.times(24, hour => <DayButton day={date} hour={hour} orderForecastDay={orderForecastDay} key={hour} />)}
              </div>
              <BinOrder date={currentDay.toISODate()} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const useOrdersDescriptionStyles = makeStyles((theme) => {
  return {
    orderText: {
      fontSize: '0.65rem',
    },
    orderTextMultipleOrders: {
      fontWeight: 'bold',
    },
    orderTextSuggestion: {
      color: theme.palette.white,
    },
  };
});

function DayOrdersDescription({ ordersForDate }) {
  const styles = useOrdersDescriptionStyles();
  const status = getOrderStatusText({ order: ordersForDate[0] });

  if (!ordersForDate.length) {
    return null;
  }

  if (ordersForDate.length > 1) {
    return (
      <span className={classnames(styles.orderText, styles.orderTextMultipleOrders)}>
        {ordersForDate.length} orders
      </span>
    );
  }

  return (
    <span className={classnames(styles.orderText, status === 'Suggestion' && styles.orderTextSuggestion)}>
      {status}
    </span>
  );
}

const useHourStyles = makeStyles((theme) => {
  return {
    futureHour: {
      background: 'white',
    },
    currentHour: {
      background: theme.palette.gray[80],
      zIndex: 2,
      margin: '-4px 0',
      borderRadius: '2px',
    },
    selectedHour: {
      background: theme.palette.teal[20],
      zIndex: 2,
      margin: '-4px 0',
      borderRadius: '2px',
    },
  };
});

function DayButton({ day, hour, orderForecastDay }) {
  const styles = useHourStyles();

  const [{ activeOrder }, dispatch] = useStateValue();

  const now = DateTime.now();
  const deliveryTime = DateTime.fromISO(activeOrder?.deliveryDate);

  const time = DateTime.fromISO(day).plus({ hours: hour });
  const diff = now.diff(time, 'minutes').values.minutes;
  const isSelectedDate = +deliveryTime.startOf('hour') === +time;

  const classes = classnames({
    [styles.currentHour]: diff > 0 && diff < 60,
    [styles.futureHour]: now < time,
    [styles.selectedHour]: isSelectedDate,
  });

  const intervalPos = Math.floor(hour / 4);
  const forecastedOrderVolume = orderForecastDay[intervalPos]?.volume || 0;
  const select = () => dispatch({ type: 'selectHour', selectedDate: time, forecastedOrderVolume });

  return <div role='button' tabIndex='-1' className={classes} onClick={select} />;
}
